import { Entypo, Feather, MaterialIcons, Octicons } from "@expo/vector-icons";
import * as Bowser from "bowser";
import isWebview from "is-ua-webview";
import {
  Box,
  Button,
  Center,
  Heading,
  Image,
  KeyboardAvoidingView,
  Popover,
  PresenceTransition,
  Row,
  Stack,
  Text,
  useTheme,
  View as NBView,
} from "native-base";
import platform from "platform-detect";
import React from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { ScreenWidth } from "react-native-elements/dist/helpers";
import { useDispatch } from "react-redux";
import usePwa from "use-pwa";

import { Images, Scale } from "../constants";
import { getAppName } from "../helpers/generalHelpers";
import { AppImage, TroubleInstallingPWAArticleComponent } from "../helpers/supportHelpers";
import { userSlice } from "../slices/userSlice";
import styles from "./LoginScreenStyles";

// General notes
// Good libraries for PWA install:
// - https://www.npmjs.com/package/@khmyznikov/pwa-install
// - https://www.npmjs.com/package/pwa-install-handler

// Expo PWA creation:
// - https://www.npmjs.com/package/expo-pwa

// NOTE: We can use the Launch Handler API (Chrome only) to add a magic link token to obviate the need for sign in
// https://developer.chrome.com/docs/web-platform/launch-handler/

// Flow:
// Android:
// [In the Instagram browser]
// - "tap the 3 dots in the top right corner"
// - "tap 'Open in Chrome'" (Dutch: "Openen in Chrome")
// - [Detect they are not in the Instagram browser]
// - "tap the 3 dots in the top right corner"
// - "tap 'Add to home screen'" (Dutch: "Toevoegen aan startscherm") ("App installeren" on safari)

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const appName = getAppName();

type Browser = "CHROME" | "SAFARI" | "SAMSUNG" | "FIREFOX" | "OPERA" | "EDGE" | "OTHER";
type InAppBrowser = "INSTAGRAM" | "FACEBOOK" | "GMAIL" | "TWITTER" | "LINKEDIN" | "WHATSAPP" | "OTHER_WEBVIEW" | "NONE";

// Development override for testing different scenarios
// Set in browser console: window.DEV_USER_AGENT = "your custom user agent string"
declare global {
  interface Window {
    DEV_USER_AGENT?: string;
  }
}

// Instagram in-app browser on Safari iOS example:
const INSTAGRAM_SAFARI_IOS_USER_AGENT_STRING =
  "Mozilla/5.0 (iPhone; CPU iPhone OS 16_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 Instagram 295.0.0.18.111 (iPhone14,7; iOS 16_6; en_US; en-US; scale=3.00; 1170x2532; 295.0.0.18.111)";

// window.DEV_USER_AGENT = INSTAGRAM_SAFARI_IOS_USER_AGENT_STRING;

// Popup placement interface
interface PopupPlacement {
  position: "top" | "bottom";
  alignment: "center" | "topRight";
  popoverPlacement:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top left"
    | "top right"
    | "bottom left"
    | "bottom right"
    | "right top"
    | "right bottom"
    | "left top"
    | "left bottom";
}

// Helper function to detect in-app browsers using Bowser
const detectInAppBrowser = (userAgent: string): InAppBrowser => {
  const browser = Bowser.getParser(userAgent);
  const browserInfo = browser.getBrowser();

  // Check for in-app browsers using Bowser's engine detection
  const browserName = browserInfo.name?.toLowerCase() || "";
  if (browserName.includes("instagram")) return "INSTAGRAM";
  if (browserName.includes("facebook")) return "FACEBOOK";
  if (browserName.includes("gmail")) return "GMAIL";
  if (browserName.includes("twitter")) return "TWITTER";
  if (browserName.includes("linkedin")) return "LINKEDIN";
  if (browserName.includes("whatsapp")) return "WHATSAPP";

  // Fallback to manual detection for browsers Bowser might not recognize
  if (userAgent.includes("Instagram")) return "INSTAGRAM";
  if (userAgent.includes("FBAN") || userAgent.includes("FBAV")) return "FACEBOOK";
  if (userAgent.includes("GSA/")) return "GMAIL";
  if (userAgent.includes("Twitter")) return "TWITTER";
  if (userAgent.includes("LinkedInApp")) return "LINKEDIN";
  if (userAgent.includes("WhatsApp")) return "WHATSAPP";

  // Use isWebview as final fallback
  if (isWebview(userAgent)) return "OTHER_WEBVIEW";

  return "NONE";
};

// Universal install instruction popup component
interface InstallInstructionPopupProps {
  browser: Browser;
  inAppBrowser: InAppBrowser;
  platform: "ios" | "android";
  isVisible: boolean;
}

const InstallInstructionPopup = ({
  browser,
  inAppBrowser,
  platform: platformProp,
  isVisible,
}: InstallInstructionPopupProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();

  const getInstallContent = (): React.ReactNode => {
    const isInWebview = inAppBrowser !== "NONE";

    const getIcon = (): JSX.Element => {
      if (isInWebview) {
        if (platformProp === "ios") {
          if (inAppBrowser === "INSTAGRAM") {
            return <Entypo name="dots-three-horizontal" size={20} color={theme.colors.gray["800"]} />;
          }
          return <MaterialIcons name="ios-share" size={20} color={theme.colors.gray["800"]} />;
        }
        return <Entypo name="dots-three-vertical" size={20} color={theme.colors.gray["800"]} />;
      }

      // Regular browser icons
      switch (browser) {
        case "CHROME":
          return platformProp === "ios" ? (
            <MaterialIcons name="ios-share" size={20} color={theme.colors.gray["800"]} />
          ) : (
            <Entypo name="dots-three-vertical" size={20} color={theme.colors.gray["800"]} />
          );
        case "SAFARI":
          return <MaterialIcons name="ios-share" size={20} color={theme.colors.gray["800"]} />;
        case "SAMSUNG":
          return <Entypo name="menu" size={20} color={theme.colors.gray["800"]} />;
        case "FIREFOX":
          return platformProp === "ios" ? (
            <MaterialIcons name="ios-share" size={20} color={theme.colors.gray["800"]} />
          ) : (
            <Entypo name="dots-three-vertical" size={20} color={theme.colors.gray["800"]} />
          );
        case "EDGE":
          return platformProp === "ios" ? (
            <MaterialIcons name="ios-share" size={20} color={theme.colors.gray["800"]} />
          ) : (
            <Entypo name="dots-three-vertical" size={20} color={theme.colors.gray["800"]} />
          );
        case "OPERA":
          return platformProp === "ios" ? (
            <MaterialIcons name="ios-share" size={20} color={theme.colors.gray["800"]} />
          ) : (
            <Entypo name="dots-three-vertical" size={20} color={theme.colors.gray["800"]} />
          );
        default:
          return platformProp === "ios" ? (
            <MaterialIcons name="ios-share" size={20} color={theme.colors.gray["800"]} />
          ) : (
            <Entypo name="dots-three-vertical" size={20} color={theme.colors.gray["800"]} />
          );
      }
    };

    const getInstructionText = (): string => {
      if (isInWebview) {
        switch (inAppBrowser) {
          case "INSTAGRAM":
            return platformProp === "ios"
              ? `${t("pwa_install_screen.box.instagram_instructions_part_1_ios")} ${t(
                  "pwa_install_screen.box.instagram_instructions_part_2_ios"
                )}`
              : `${t("pwa_install_screen.box.instagram_instructions_part_1_android")} ${t(
                  "pwa_install_screen.box.instagram_instructions_part_2_android_webview"
                )}`;
          case "FACEBOOK":
          case "GMAIL":
          case "TWITTER":
          case "LINKEDIN":
          case "WHATSAPP":
          default:
            // For other in-app browsers, use similar Instagram instructions
            return platformProp === "ios"
              ? `${t("pwa_install_screen.box.instagram_instructions_part_1_ios")} ${t(
                  "pwa_install_screen.box.instagram_instructions_part_2_ios"
                )}`
              : `${t("pwa_install_screen.box.instagram_instructions_part_1_android")} ${t(
                  "pwa_install_screen.box.instagram_instructions_part_2_android_webview"
                )}`;
        }
      }

      // Regular browser instructions
      if (platformProp === "ios") {
        if (browser === "SAFARI") {
          return `${t("pwa_install_screen.box.install_instructions_part_1_ios_safari")} ${t(
            "pwa_install_screen.box.install_instructions_part_2_ios_safari"
          )}`;
        }
        // All non-Safari iOS browsers (Chrome, Firefox, Edge, Opera) use the share button
        return `${t("pwa_install_screen.box.install_instructions_part_1_ios_non_safari")} ${t(
          "pwa_install_screen.box.install_instructions_part_2_ios_non_safari"
        )}`;
      }

      // Android
      if (browser === "SAMSUNG") {
        return `${t("pwa_install_screen.box.install_instructions_part_1_android_samsung")} ${t(
          "pwa_install_screen.box.install_instructions_part_2_android_samsung"
        )}`;
      }
      return `${t("pwa_install_screen.box.install_instructions_part_1_android")} ${t(
        "pwa_install_screen.box.install_instructions_part_2_android"
      )}`;
    };

    return (
      <Row alignItems="center" space={3}>
        {getIcon()}
        <Box flex={1}>
          <Text
            fontSize="md"
            flexShrink={1}
            color="coolGray.700"
            _dark={{ color: "coolGray.200" }}
            lineHeight="md"
            fontWeight="medium"
          >
            {getInstructionText()}
          </Text>
        </Box>
      </Row>
    );
  };

  const getPopupPlacement = (): PopupPlacement => {
    // For in-app browsers, show at top right pointing to menu
    if (inAppBrowser !== "NONE") {
      return {
        position: "top",
        alignment: "topRight",
        popoverPlacement: "bottom left",
      };
    }

    // For iOS non-Safari browsers, show at top right pointing to share button in address bar
    if (platformProp === "ios" && browser !== "SAFARI") {
      return {
        position: "top",
        alignment: "topRight",
        popoverPlacement: "bottom left",
      };
    }

    // For Chrome on Android, show at top right pointing to menu
    if (platformProp === "android" && browser === "CHROME") {
      return {
        position: "top",
        alignment: "topRight",
        popoverPlacement: "bottom left",
      };
    }

    // For Safari iOS and other Android browsers, show at bottom center
    return {
      position: "bottom",
      alignment: "center",
      popoverPlacement: "top",
    };
  };

  const placement = getPopupPlacement();

  return (
    <InstallMessagePopup
      content={getInstallContent()}
      placement={placement}
      browser={browser}
      platform={platformProp}
      isVisible={isVisible}
    />
  );
};

// Reusable popup component for install messages
interface InstallMessagePopupProps {
  content: React.ReactNode;
  placement: PopupPlacement;
  browser: Browser;
  platform: "ios" | "android";
  isVisible?: boolean;
}

const InstallMessagePopup = ({
  content,
  placement,
  browser,
  platform: platformType,
  isVisible = true,
}: InstallMessagePopupProps): JSX.Element => {
  const theme = useTheme();
  // Only show arrow for center alignments to avoid positioning issues, but not for Samsung browser
  const showArrow = placement.alignment === "center" && !(platformType === "android" && browser === "SAMSUNG");
  const getPositionStyles = (): {
    position: "absolute";
    bottom?: string;
    top?: string;
    right?: string;
    alignSelf?: "center";
    zIndex: number;
    pointerEvents: "none";
  } => {
    if (placement.position === "bottom") {
      return {
        position: "absolute" as const,
        bottom: `${theme.space["2"]}px`,
        alignSelf: "center" as const,
        zIndex: 10,
        pointerEvents: "none" as const,
      };
    }

    // top position
    if (placement.alignment === "topRight") {
      return {
        position: "absolute" as const,
        top: `${theme.space["2"]}px`,
        right: `${theme.space["2"]}px`,
        zIndex: 10,
        pointerEvents: "none" as const,
      };
    }

    return {
      position: "absolute" as const,
      top: `${theme.space["2"]}px`,
      alignSelf: "center" as const,
      zIndex: 10,
      pointerEvents: "none" as const,
    };
  };

  if (!isVisible) return <></>;

  return (
    <Box {...getPositionStyles()}>
      <Popover
        isOpen={true}
        placement={placement.popoverPlacement}
        trigger={(triggerProps) => <Box {...triggerProps} h={1} w={1} />}
        _backdrop={{ bg: "transparent", pointerEvents: "none" }}
      >
        <Popover.Content
          bg="white"
          shadow={3}
          borderRadius={theme.borderRadii.modal.default}
          w="auto"
          maxW="90%"
          alignSelf="center"
          pointerEvents="auto"
          borderWidth="1"
          borderColor="coolGray.200"
          _dark={{
            bg: "coolGray.800",
            borderColor: "coolGray.600",
          }}
        >
          {Boolean(showArrow) && <Popover.Arrow />}
          <Popover.Body paddingX={6} paddingY={5}>
            {content}
          </Popover.Body>
        </Popover.Content>
      </Popover>
    </Box>
  );
};

const PWASignupScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [deferredPrompt, setDeferredPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);

  const { appinstalled, canInstallprompt, enabledA2hs, enabledPwa, isLoading, isPwa, showInstallPrompt, userChoice } =
    usePwa();

  console.trace("PWASignupScreen: Initial usePwa state", {
    appinstalled,
    canInstallprompt,
    enabledA2hs,
    enabledPwa,
    isLoading,
    isPwa,
    userChoice,
  });

  React.useEffect(() => {
    console.log("PWASignupScreen: useEffect for beforeinstallprompt setup");
    const handler = (e: BeforeInstallPromptEvent): void => {
      console.trace("PWASignupScreen: beforeinstallprompt event handler started", e);
      // Prevent the browser's default install prompt.
      e.preventDefault();
      console.trace("PWASignupScreen: Browser's default prompt prevented.");

      // Stash the event. It might still be useful for detecting installability,
      // but calling e.prompt() later may not work if the browser already prompted.
      console.trace("PWASignupScreen: Stashing the event.");
      setDeferredPrompt(e);
      console.trace("PWASignupScreen: beforeinstallprompt event captured. Browser may show its own install UI.");
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      console.trace("PWASignupScreen: useEffect for beforeinstallprompt cleanup");
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  React.useEffect(() => {
    console.trace("PWASignupScreen: deferredPrompt state changed", deferredPrompt);
  }, [deferredPrompt]);

  // Get user agent with development override support
  const getUserAgent = (): string => {
    if (typeof window === "undefined") return "";

    return window.DEV_USER_AGENT || navigator.userAgent;
  };

  const userAgent = getUserAgent();
  const inAppBrowser: InAppBrowser = detectInAppBrowser(userAgent);
  const inWebview: boolean = inAppBrowser !== "NONE";

  const browser: Browser = (() => {
    if (typeof window === "undefined") {
      return "OTHER";
    }

    const bowserParser = Bowser.getParser(userAgent);
    const browserInfo = bowserParser.getBrowser();

    // Use Bowser for browser detection with fallback to platform-detect
    const browserName = browserInfo.name?.toLowerCase() || "";
    switch (browserName) {
      case "chrome":
        return "CHROME";
      case "safari":
        return "SAFARI";
      case "samsung internet":
      case "samsung browser":
        return "SAMSUNG";
      case "firefox":
        return "FIREFOX";
      case "opera":
        return "OPERA";
      case "microsoft edge":
      case "edge":
        return "EDGE";
      default:
        // Fallback to platform-detect for edge cases
        if (platform.samsungBrowser) return "SAMSUNG";
        if (platform.chrome) return "CHROME";
        if (platform.safari) return "SAFARI";
        if (platform.firefox) return "FIREFOX";
        if (platform.opera) return "OPERA";
        if (platform.edge) return "EDGE";
        return "OTHER";
    }
  })();

  const isiOS = typeof window !== "undefined" ? platform.ios : false;
  const isPWA = typeof window !== "undefined" ? platform.pwa : false;
  const platformType: "ios" | "android" = isiOS ? "ios" : "android";

  // Log current state and override examples for development
  React.useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("PWA Development Override Examples:");
      console.log(
        "Instagram iOS:",
        'window.DEV_USER_AGENT = "Mozilla/5.0 (iPhone; CPU iPhone OS 16_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 Instagram 295.0.0.18.111 (iPhone14,7; iOS 16_6; en_US; en-US; scale=3.00; 1170x2532; 295.0.0.18.111)"'
      );
      console.log("Clear override:", "window.DEV_USER_AGENT = undefined");
      console.log("Current state:", {
        browser,
        inAppBrowser,
        platformType,
        isPWA,
        canInstallprompt,
        userAgent: `${userAgent.slice(0, 50)}...`,
      });
    }
  }, [browser, inAppBrowser, platformType, isPWA, canInstallprompt, userAgent]);

  const imageSize = Scale(ScreenWidth * 0.8 * 0.5);

  function onSubmitDeferPwaInstallPrompt(): void {
    dispatch(userSlice.actions.deferPwaInstallPrompt());
  }

  // Determine when to show install instructions
  const shouldShowInstallPopup = (() => {
    // Don't show if already PWA
    if (isPWA) {
      return false;
    }

    // Don't show if native install button is available
    if (deferredPrompt && canInstallprompt) {
      return false;
    }

    // Show for in-app browsers that need to be opened in regular browser
    if (inWebview) {
      return true;
    }

    // For Android on Samsung Browser, always show instructions if not a PWA and no native prompt.
    if (!isiOS && browser === "SAMSUNG") {
      return true;
    }

    // For iOS Safari, show popup instead of old banner
    if (isiOS && browser === "SAFARI") {
      return true;
    }

    // For other browsers, show if can install
    if (!isiOS && canInstallprompt) {
      return true;
    }

    // For iOS non-Safari browsers, still show instructions
    if (isiOS && browser !== "SAFARI") {
      return true;
    }

    return false;
  })();

  return (
    <View style={{ ...styles.wrapper, backgroundColor: theme.colors.primary[10] }}>
      <View style={{ ...styles.loginContainer, marginTop: -200 }}></View>
      <View style={styles.container}>
        <KeyboardAvoidingView behavior="position">
          <Center>
            <AppImage />

            {/* Button to manually trigger the PWA install prompt */}
            {deferredPrompt && canInstallprompt ? (
              <Button
                onPress={() => {
                  if (deferredPrompt) {
                    console.trace("PWASignupScreen: Manual install button clicked. Attempting to call prompt().");
                    deferredPrompt.prompt().catch((err) => {
                      // Handle or log potential errors from the prompt() call itself, if necessary.
                      console.error("Error during prompt():", err);
                    });
                    // Wait for the user to respond to the prompt
                    deferredPrompt.userChoice
                      .then((choiceResult) => {
                        console.trace("PWASignupScreen: userChoice after manual prompt:", choiceResult);
                        if (choiceResult.outcome === "accepted") {
                          console.trace("PWASignupScreen: User accepted the A2HS prompt (manual)");
                        } else {
                          console.trace("PWASignupScreen: User dismissed the A2HS prompt (manual)");
                        }
                        // Clear the deferredPrompt so the button disappears after interaction.
                        setDeferredPrompt(null);
                      })
                      .catch((error: Error) => {
                        console.error("PWASignupScreen: Error in userChoice.then (manual prompt)", error);
                        setDeferredPrompt(null); // Also clear on error
                      });
                  }
                }}
                mt="6"
                size="lg"
                bg="primary.500"
                _hover={{ bg: "primary.600" }}
                _pressed={{ bg: "primary.700" }}
                rounded={theme.borderRadii.button.pill}
                px="16"
                py="5"
                shadow={3}
                borderWidth="0"
                leftIcon={<MaterialIcons name="file-download" size={24} color="white" />}
                _text={{
                  color: "white",
                  fontSize: "lg",
                  fontWeight: "bold",
                  letterSpacing: "0.5",
                }}
              >
                {t("pwa_install_screen.install_app_button_text")}
              </Button>
            ) : null}

            <NBView marginTop="8">
              <TroubleInstallingPWAArticleComponent />
            </NBView>

            {/* We used to allow the user to proceed this way to access on web.
            But this causes problems because people then login in an in-app browser.
            We want to prevent this. */}

            {/* <PresenceTransition
              visible={true}
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
                transition: {
                  duration: 250,
                  delay: 5000,
                },
              }}
            >
              <Button onPress={onSubmitDeferPwaInstallPrompt} mt="8" size="sm" variant="outline">
                <Text>{t("pwa_install_screen.defer_installation_button_text")}</Text>
              </Button>
            </PresenceTransition> */}
          </Center>
        </KeyboardAvoidingView>
      </View>

      {/* Universal install instruction popup */}
      {Boolean(shouldShowInstallPopup) && (
        <InstallInstructionPopup
          browser={browser}
          inAppBrowser={inAppBrowser}
          platform={platformType}
          isVisible={shouldShowInstallPopup}
        />
      )}
    </View>
  );
};

export default PWASignupScreen;
